import React from "react";

import { Check } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { BulletinSectionId } from "@/components/alerts/add-v2/sections/sectionIds";

import { SectionRailItem } from "./SectionRail";

interface SectionStepperProps {
  items: SectionRailItem[];
  activeId: BulletinSectionId | null;
  onJump: (id: BulletinSectionId) => void;
  progressLabel: string;
}

/**
 * The xs replacement for the rail: a horizontal numbered stepper.
 *
 * Captions are dropped at this width — the numbers plus the open section's own heading carry the
 * position — so the six steps fit without horizontal scrolling on a phone.
 */
export const SectionStepper: React.FC<SectionStepperProps> = ({
  items,
  activeId,
  onJump,
  progressLabel,
}) => {
  return (
    <Box
      component="nav"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        backgroundColor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        px: 2,
        py: 1.25,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {progressLabel}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", mt: 0.75 }}>
        {items.map((item, index) => (
          <React.Fragment key={item.id}>
            <Box
              component="button"
              type="button"
              onClick={() => onJump(item.id)}
              aria-label={item.label}
              aria-current={item.id === activeId ? "step" : undefined}
              sx={{
                flex: "0 0 auto",
                width: 24,
                height: 24,
                p: 0,
                border: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // `fontFamily`, not the `font` shorthand: the shorthand resets `font-size`, and
                // sx emits in key order, so it cancelled the 12 above and the numbers came out
                // at body size inside a 24px circle. Only the family needs overriding — a
                // `<button>` does not inherit it.
                fontFamily: "inherit",
                fontSize: 12,
                lineHeight: 1,
                cursor: "pointer",
                color: "common.white",
                backgroundColor:
                  item.complete || item.id === activeId
                    ? "primary.main"
                    : "action.disabled",
              }}
            >
              {item.complete ? <Check sx={{ fontSize: 14 }} /> : index + 1}
            </Box>
            {index < items.length - 1 && (
              <Box
                aria-hidden
                sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: "#bdbdbd",
                  mx: 1,
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
